import { ConditionGroupType } from '../../contexts/PatternTreeContext'
import { compilePattern } from '../compile/compilePattern'
import { getRenderMeta } from '../render/renderMeta'
import { CompileWarning, SeriesId } from '../types'
import { SolvedChart, assignValues } from './assignValues'
import { solveSelection } from './disjunctions'

export interface PreviewResult {
  status: 'ok' | 'conflict' | 'empty' | 'unverified'
  charts: SolvedChart[]
  /** condition ids proven mutually contradictory — drives amber card outlines */
  conflictConditionIds: string[]
  /** conditions dropped to satisfy N-of-M group counts — rendered as "or:" chips */
  droppedConditionIds: string[]
  warnings: CompileWarning[]
}

const EMPTY: PreviewResult = {
  status: 'empty',
  charts: [],
  conflictConditionIds: [],
  droppedConditionIds: [],
  warnings: [],
}

/**
 * The single entry point the preview panel calls: compile → solve → assign.
 * Never throws — an internal error degrades to the empty state so the editor
 * can never crash because of the preview (spec §10).
 */
export const solvePattern = (
  groups: ConditionGroupType[],
  patternId: string,
  flatSeries?: Set<SeriesId>
): PreviewResult => {
  try {
    const model = compilePattern(groups)
    if (model.conditionIds.length === 0) {
      return { ...EMPTY, warnings: model.warnings }
    }
    const selection = solveSelection(model)
    if (!selection.ok) {
      const conflictConditionIds = Array.from(
        new Set(
          (selection.conflict?.provenance ?? [])
            .map((provenance) => provenance.conditionId)
            .filter((id): id is string => id !== undefined)
        )
      )
      // A capped/truncated search has not PROVEN anything; likewise a failure
      // touching a first-option-flattened windowed condition may be an
      // artifact of the flattening. Both degrade to the honest 'unverified'
      // state instead of banner + amber outlines on a satisfiable pattern.
      const provedNothing =
        selection.unverified ||
        conflictConditionIds.some((id) => model.flattenedWindowIds.includes(id))
      if (provedNothing) {
        return {
          status: 'unverified',
          charts: [],
          conflictConditionIds: [],
          droppedConditionIds: [],
          warnings: model.warnings,
        }
      }
      return {
        status: 'conflict',
        charts: [],
        conflictConditionIds,
        droppedConditionIds: [],
        warnings: model.warnings,
      }
    }
    // Level-style series (pivot points, PSAR) hold flat instead of wiggling.
    const resolvedFlatSeries =
      flatSeries ??
      new Set(
        Object.values(model.charts).flatMap((chart) =>
          Object.entries(chart.seriesInfo)
            .filter(([, info]) => getRenderMeta(info.indicatorName, info.field).style === 'level')
            .map(([seriesId]) => seriesId)
        )
      )
    const charts = assignValues(model, selection, patternId, resolvedFlatSeries)
    return {
      status: selection.unverified ? 'unverified' : 'ok',
      charts,
      conflictConditionIds: [],
      droppedConditionIds: selection.chosen.droppedConditionIds,
      warnings: model.warnings,
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('pattern preview failed to solve', error)
    return EMPTY
  }
}
